import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Typography,
} from "antd";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import {
    MembershipCategoryRow,
    PAYMENT_FREQUENCY_OPTIONS,
    PaymentFrequency,
} from "../../../model/members/MembershipCategoryRow";
import { CreateMembershipCategoryRequest } from "../../../requests/member/CreateMembershipCategoryRequest";
import { UpdateMembershipCategoryRequest } from "../../../requests/member/UpdateMembershipCategoryRequest";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";

const { Text } = Typography;

interface CategoryFormValues {
    name: string;
    description: string;
    price: number;
    paymentFrequency: PaymentFrequency;
    isJointMembership: boolean;
    isVisible: boolean;
}

const EMPTY_FORM: CategoryFormValues = {
    name: '',
    description: '',
    price: 0,
    paymentFrequency: 'Monthly',
    isJointMembership: false,
    isVisible: true,
};

const toFormValues = (row: MembershipCategoryRow): CategoryFormValues => ({
    name: row.name,
    description: row.description ?? '',
    price: row.price,
    paymentFrequency: row.paymentFrequency,
    isJointMembership: row.isJointMembership,
    isVisible: row.isVisible,
});

const toRequest = (values: CategoryFormValues): CreateMembershipCategoryRequest => ({
    name: values.name.trim(),
    // Trim then collapse-empty to null so the API persists NULL instead of an
    // empty string — keeps the description column meaningful for filters.
    description: values.description.trim() === '' ? null : values.description.trim(),
    price: values.price,
    paymentFrequency: values.paymentFrequency,
    isJointMembership: values.isJointMembership,
    isVisible: values.isVisible,
});

// Settings panel for the per-client member-category CRUD. Ignores the
// controlled value/onChange props because this section owns a list of rows,
// not a single value — it loads/persists each row directly through the
// settings service rather than bubbling state up to the shell.
export const MemberCategoriesPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;

    const [rows, setRows] = useState<MembershipCategoryRow[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const [editingId, setEditingId] = useState<number | 'new' | null>(null);
    const [draft, setDraft] = useState<CategoryFormValues>(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const refresh = useCallback(async () => {
        if (!Number.isFinite(clientId)) return;
        try {
            const next = await service.listMemberCategories(clientId);
            setRows(next);
            setLoadError(null);
        } catch (err) {
            setLoadError(err instanceof Error ? err.message : 'Failed to load categories.');
        }
    }, [service, clientId]);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            setLoadError('Invalid client id in URL.');
            return;
        }
        setLoadError(null);
        void refresh();
    }, [clientId, refresh]);

    const handleOpenCreate = () => {
        setDraft(EMPTY_FORM);
        setEditingId('new');
        setActionError(null);
    };

    const handleOpenEdit = (row: MembershipCategoryRow) => {
        setDraft(toFormValues(row));
        setEditingId(row.id);
        setActionError(null);
    };

    const handleCloseModal = () => {
        if (saving) return;
        setEditingId(null);
        setActionError(null);
    };

    const handleSave = async () => {
        if (editingId === null) return;
        if (draft.name.trim() === '') {
            setActionError('Name is required.');
            return;
        }
        setSaving(true);
        setActionError(null);
        try {
            if (editingId === 'new') {
                await service.createMemberCategory(clientId, toRequest(draft));
            } else {
                const payload: UpdateMembershipCategoryRequest = toRequest(draft);
                await service.updateMemberCategory(clientId, editingId, payload);
            }
            setEditingId(null);
            await refresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to save category.');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleVisible = useCallback(async (row: MembershipCategoryRow) => {
        try {
            await service.updateMemberCategory(clientId, row.id, {
                name: row.name,
                description: row.description,
                price: row.price,
                paymentFrequency: row.paymentFrequency,
                isJointMembership: row.isJointMembership,
                isVisible: !row.isVisible,
            });
            await refresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to toggle visibility.');
        }
    }, [service, clientId, refresh]);

    const handleDelete = useCallback(async (row: MembershipCategoryRow) => {
        try {
            await service.deleteMemberCategory(clientId, row.id);
            await refresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to delete category.');
        }
    }, [service, clientId, refresh]);

    const updateDraft = <K extends keyof CategoryFormValues>(key: K, value: CategoryFormValues[K]) => {
        setDraft(prev => ({ ...prev, [key]: value }));
    };

    const columns = useMemo(() => [
        {
            title: 'Name',
            dataIndex: 'name' as const,
            render: (name: string, row: MembershipCategoryRow) => (
                <Space>
                    <span data-testid={`mc-row-name-${row.id}`}>{name}</span>
                    {!row.isVisible && <Text type="secondary">(hidden)</Text>}
                </Space>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description' as const,
            render: (description: string | null) => description ?? <Text type="secondary">—</Text>,
        },
        {
            title: 'Price',
            dataIndex: 'price' as const,
            render: (price: number) => price.toFixed(2),
        },
        {
            title: 'Frequency',
            dataIndex: 'paymentFrequency' as const,
        },
        {
            title: 'Joint',
            dataIndex: 'isJointMembership' as const,
            render: (joint: boolean) => (joint ? 'Yes' : 'No'),
        },
        {
            title: 'Visible',
            dataIndex: 'isVisible' as const,
            render: (visible: boolean, row: MembershipCategoryRow) => (
                <Switch
                    checked={visible}
                    onChange={() => handleToggleVisible(row)}
                    data-testid={`mc-toggle-visible-${row.id}`}
                />
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, row: MembershipCategoryRow) => (
                <Space>
                    <Button
                        size="small"
                        data-testid={`mc-edit-${row.id}`}
                        onClick={() => handleOpenEdit(row)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete this category?"
                        description="Members already on this category will block deletion. Hide it instead if you want to keep them."
                        okText="Delete"
                        cancelText="Cancel"
                        onConfirm={() => handleDelete(row)}
                    >
                        <Button size="small" danger data-testid={`mc-delete-${row.id}`}>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ], [handleDelete, handleToggleVisible]);

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="mc-load-error" />;
    }
    if (rows === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                <Button
                    type="primary"
                    data-testid="mc-add-btn"
                    onClick={handleOpenCreate}
                >
                    Add category
                </Button>
            </Space>
            {actionError && (
                <Alert type="error" message={actionError} data-testid="mc-action-error" />
            )}
            <Table
                rowKey="id"
                dataSource={rows}
                columns={columns}
                pagination={false}
                data-testid="mc-table"
                locale={{ emptyText: 'No member categories yet.' }}
            />
            <Modal
                open={editingId !== null}
                title={editingId === 'new' ? 'Add member category' : 'Edit member category'}
                onCancel={handleCloseModal}
                onOk={handleSave}
                okText="Save"
                confirmLoading={saving}
                okButtonProps={{
                    disabled: draft.name.trim() === '',
                    'data-testid': 'mc-modal-save',
                } as any}
                cancelButtonProps={{ disabled: saving }}
                destroyOnClose
            >
                <Form layout="vertical" data-testid="mc-form">
                    <Form.Item
                        label="Name"
                        validateStatus={draft.name.trim() === '' ? 'error' : undefined}
                        help={draft.name.trim() === '' ? 'Name is required.' : undefined}
                    >
                        <Input
                            data-testid="mc-form-name"
                            value={draft.name}
                            onChange={(e) => updateDraft('name', e.target.value)}
                            autoFocus
                            maxLength={100}
                        />
                    </Form.Item>
                    <Form.Item label="Description">
                        <Input.TextArea
                            data-testid="mc-form-description"
                            value={draft.description}
                            onChange={(e) => updateDraft('description', e.target.value)}
                            rows={2}
                            maxLength={2000}
                        />
                    </Form.Item>
                    <Form.Item label="Price">
                        <InputNumber
                            data-testid="mc-form-price"
                            inputMode="decimal"
                            value={draft.price}
                            min={0}
                            step={0.01}
                            precision={2}
                            style={{ width: '100%' }}
                            onChange={(value) => updateDraft('price', value ?? 0)}
                        />
                    </Form.Item>
                    <Form.Item label="Payment frequency">
                        <Select<PaymentFrequency>
                            data-testid="mc-form-frequency"
                            value={draft.paymentFrequency}
                            onChange={(value) => updateDraft('paymentFrequency', value)}
                            options={PAYMENT_FREQUENCY_OPTIONS as unknown as { value: PaymentFrequency; label: string }[]}
                        />
                    </Form.Item>
                    <Form.Item label="Joint membership">
                        <Switch
                            data-testid="mc-form-joint"
                            checked={draft.isJointMembership}
                            onChange={(checked) => updateDraft('isJointMembership', checked)}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Visible on signup"
                        help="Hide a category to retire it without breaking existing members."
                    >
                        <Switch
                            data-testid="mc-form-visible"
                            checked={draft.isVisible}
                            onChange={(checked) => updateDraft('isVisible', checked)}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    );
};
